import { SnackbarProps } from '@mui/material';
import dayjs from 'dayjs';
import { Client } from 'urql';
import { mapFieldValuesToId } from 'components/map-field-values-to-ids';
import { formatDateString, parseDateString } from 'loc/date';
import { clearPendingQuoteLine } from 'hooks/pendingQuoteLine';
import { SnackbarVariant } from 'hooks/useSnackBar';
import toastToSnackbar from 'utils/toast_snackbar';
import {
  CascadingSelectOptionsDocument,
  CascadingSelectOptionsQuery,
  Field,
  FieldGroupFragment as FieldGroup,
  FieldInput,
  FieldSetFragment as FieldSet,
  FormFragment as Form,
  FormAutoSaveDocument,
  FormAutoSaveMutation,
  FormInput,
  FormType,
  InputType,
  Maybe,
  MutationFormAutoSaveArgs,
  QueryCascadingSelectOptionsArgs,
  ShowIf,
} from 'generated/graphql';

export const buildUpdatedForm = (original: any, field: Pick<Field, 'id'>, value: string, values?: string[]): Form => ({
  ...original,
  fields: original.fields.map((f: Field) => {
    if (f.id === field.id) {
      return {
        ...f,
        value,
        values: values ?? [value].filter(Boolean),
      };
    }
    return f;
  }),
  fieldGroups: original.fieldGroups.map((group: FieldGroup) => ({
    ...group,
    fields: group.fields.map((f) => {
      if (f.id === field.id) {
        return {
          ...f,
          value,
          values: values ?? [value].filter(Boolean),
        };
      }
      return f;
    }),
  })),
  fieldSets: original.fieldSets.map((fieldSet: FieldSet) => ({
    ...fieldSet,
    groups: fieldSet.groups.map((group) => ({
      ...group,
      fields: group.fields.map((f) => {
        if (f.id === field.id) {
          return {
            ...f,
            value,
            values: values ?? [value].filter(Boolean),
          };
        }
        return f;
      }),
    })),
  })),
});

const parseField = (field: Field): Field => {
  if (field.type === InputType.Date) {
    return {
      ...field,
      value: parseDateString(field.value),
    };
  }

  return field;
};

const formatField = (field: Field): Field => {
  if (field.type === InputType.Date) {
    return {
      ...field,
      value: formatDateString(field.value),
    };
  }

  return field;
};

export const parseForm = (original: Form): Form => ({
  ...original,
  fields: original.fields.map((f) => ({
    ...f,
    value: parseField(f).value,
  })),
  fieldGroups: original.fieldGroups.map((group) => ({
    ...group,
    fields: group.fields.map((f) => ({
      ...f,
      value: parseField(f).value,
    })),
  })),
  fieldSets: original.fieldSets.map((fieldSet) => ({
    ...fieldSet,
    groups: fieldSet.groups.map((group) => ({
      ...group,
      fields: group.fields.map((f) => ({
        ...f,
        value: parseField(f).value,
      })),
    })),
  })),
});

// Required fields carry an asterisk so the form shows what the server's CheckRequired
// enforces. MUI inputs take the `required` prop instead and render the same mark
// themselves; this is for the controls whose label is a plain string.
export const requiredLabel = (label: string, required: boolean) => (required && label ? `${label} *` : label);

export const showField = (field: Field, allFields: Field[]): boolean => {
  if (field.showIf?.fieldId) {
    const leadField = allFields.find((item) => item.id === field.showIf?.fieldId);
    if (!leadField) {
      return false;
    }

    if (!field.showIf.hasValues.includes(leadField.value)) {
      return false;
    }
  }

  return true;
};

// Evaluates a single condition of the current field against all field values in form
const evaluateShowIf = (condition: Maybe<ShowIf>, allFields: Field[]): boolean => {
  // If no field ID is defined for condition, skip this condition
  if (!condition?.fieldId) return true;

  // Find relevant controlling field in the form
  const leadField = allFields.find((item) => item.id === condition.fieldId);

  // Conditon passes if the controlling field exists and its value matches one of the expected values
  return !!(leadField && condition.hasValues.includes(leadField.value));
};

// [NEW] Function to determine if a field should show in the form
export const showFieldNew = (field: Field, allFields: Field[]): boolean => {
  // List of ShowIf conditions
  const conditions = field?.showIfs?.ifs;

  // If no conditions are defined, always show the field
  if (!conditions || conditions.length === 0) return true;

  // Get the logical operator, default to 'AND'
  const operator = field.showIfs?.operator?.toUpperCase() ?? 'AND';

  switch (operator) {
    case 'AND':
      // All conditions must pass
      return conditions.every((condition) => evaluateShowIf(condition, allFields));
    case 'OR':
      // At least one condition must pass
      return conditions.some((condition) => evaluateShowIf(condition, allFields));
    default:
      // Unknown operator — fallback to visible
      return true;
  }
};

export const showGroup = (group: FieldGroup) => {
  return group.cards.length > 0 || group.fields.some((field) => field.type !== InputType.Hidden);
};

export const showSet = (groups: FieldGroup[]) => {
  return groups.some((group) => showGroup(group));
};

export const getFormValues = async (formData: any): Promise<FieldInput[]> => {
  const mapField = async (field: Field) => {
    const formattedField = formatField(field);

    if (field.type === InputType.Multiselect) {
      return {
        id: field.id,
        value: formattedField.value,
        values: formattedField.values,
        entityID: field.entityID,
        fieldGroupID: field.fieldGroupID,
      };
    }

    if (field.type === InputType.Date) {
      return {
        id: field.id,
        value: formattedField.value,
        values: [formattedField.value],
        entityID: field.entityID,
        fieldGroupID: field.fieldGroupID,
      };
    }

    if (field.type === InputType.Calculated || field.type === InputType.CalculatedCurrency) {
      const { evaluate } = await import('services/math');

      // TODO: Remove once we move to fieldSets everywhere
      const allFields =
        formData?.fieldSets?.length > 0
          ? formData.fieldSets.flatMap((set: FieldSet) => set.groups.flatMap((group) => group.fields))
          : formData?.fieldGroups?.length > 0
            ? formData.fieldGroups.flatMap((group: FieldGroup) => group.fields)
            : formData.fields;

      const nameValueMap = mapFieldValuesToId(allFields);

      const expression = field.values.length > 0 ? field.values[0] : field.value;
      let value = formattedField.value;
      try {
        const result = evaluate(expression, nameValueMap);
        if (typeof result === 'number' && !Number.isNaN(result)) {
          value = result.toString();
        } else {
          value = '';
        }
      } catch (err) {
        console.warn('[getFormValues] failed to evaluate calculated field', {
          fieldId: field.id,
          expression,
          err,
        });
      }
      return {
        id: field.id,
        value: value,
        values: [],
        entityID: field.entityID,
        fieldGroupID: field.fieldGroupID,
      };
    }

    return {
      id: field.id,
      value: formattedField.value,
      values:
        formattedField.values.length > 0 ? formattedField.values : formattedField.value.split(';').filter(Boolean),
      entityID: field.entityID,
      fieldGroupID: field.fieldGroupID,
    };
  };
  const fields = await Promise.all(formData.fields.map(mapField));
  const groupedFields: Awaited<ReturnType<typeof mapField>>[] = [];
  for (let group of formData.fieldGroups) {
    const fields = await Promise.all(group.fields.map(mapField));
    groupedFields.push(...fields);
  }

  const fieldSetFields: Awaited<ReturnType<typeof mapField>>[] = [];
  for (let fieldSet of formData.fieldSets) {
    for (let group of fieldSet.groups) {
      const filtered = group.fields.filter(
        (field: Field) => (field.value && field.value.length > 0) || field.values.length > 0 || !field.fieldGroupID,
      );
      const fields = await Promise.all(filtered.map(mapField));
      fieldSetFields.push(...fields);
    }
  }

  return [...fields, ...groupedFields, ...fieldSetFields];
};

export const replaceFormFields = (form: Form, fields: Field[]): Form => {
  const fieldDictionary = fields.reduce<{ [id: string]: Field }>(
    (acc, val) => ({
      ...acc,
      [val.id]: val,
    }),
    {},
  );
  return {
    ...form,
    fields: form.fields.map((field) => fieldDictionary[field.id] || field),
    fieldGroups: form.fieldGroups.map((group) => ({
      ...group,
      fields: group.fields.map((field) => fieldDictionary[field.id] || field),
    })),
    fieldSets: form.fieldSets.map((fieldSet) => ({
      ...fieldSet,
      groups: fieldSet.groups.map((group) => ({
        ...group,
        fields: group.fields.map((field) => fieldDictionary[field.id] || field),
      })),
    })),
  };
};

const getAllFormFields = (form: Form): Field[] => [
  ...form.fields,
  ...form.fieldGroups.flatMap((group) => group.fields),
  ...form.fieldSets.flatMap((fieldSet) => fieldSet.groups.flatMap((group) => group.fields)),
];

const hasSameFieldValue = (field: Field, other: Field) =>
  (field.value ?? '') === (other.value ?? '') &&
  JSON.stringify(field.values ?? []) === JSON.stringify(other.values ?? []);

// Re-applies fields the user changed since `previous` (the form `local` started from) onto a refetched form
export const keepLocalFormEdits = (refetched: Form, local?: Form, previous?: Form): Form => {
  if (!local || !previous) return refetched;

  const previousFields = new Map(getAllFormFields(previous).map((field) => [field.id, field]));
  const editedFields = getAllFormFields(local).filter((field) => {
    const previousField = previousFields.get(field.id);
    return !!previousField && !hasSameFieldValue(field, previousField);
  });

  return editedFields.reduce((form, field) => buildUpdatedForm(form, field, field.value, field.values), refetched);
};

export const getFormState = (data: Form) => {
  return {
    ...data,
    fields: data.fields.map((f) =>
      f.type === InputType.Images
        ? {
            ...f,
            values: f.value.length > 0 ? [...f.values, f.value] : f.values,
          }
        : f,
    ),
  };
};

type UpdateNewFieldParams = {
  fieldGroup: Field[];
  field: Field;
  value: string;
  values?: string[];
  entityID?: string;
  editIDs?: string[];
  formType: FormType;
  client: Client;
  setFormState: React.Dispatch<React.SetStateAction<Form | undefined>>;
  setLoading: (loading: boolean) => void;
  onRefreshForm: () => void;
  showSnackbar: (props: SnackbarProps, variant?: SnackbarVariant) => void;
  isQuotesSearchEnabled?: boolean;
};

export const buildAutoSaveFieldInputGroup = async (
  fieldGroup: Field[],
  field: Field,
  value: string,
  values?: string[],
  entityID?: string,
): Promise<FormInput['fields']> => {
  const isDateField = (f: Field) => f.type === InputType.Date;
  const isCalculatedField = (f: Field) => f.type === InputType.Calculated || f.type === InputType.CalculatedCurrency;
  const toISOSafeString = (val: string) => dayjs(val).toISOString();

  const fieldGroupsCopy = !fieldGroup || fieldGroup.length === 0 ? [field] : fieldGroup;

  // Set values too: the expression map reads values[0] first, so a stale array prices sales off the previous keystroke.
  const resolvedGroup = fieldGroupsCopy.map((f) =>
    f.id === field.id ? { ...f, value, values: values ?? [value].filter(Boolean) } : f,
  );

  const evaluateExpression = resolvedGroup.some(isCalculatedField)
    ? (await import('services/math')).evaluate
    : undefined;
  const nameValueMap = evaluateExpression ? mapFieldValuesToId(resolvedGroup) : {};

  const evaluateCalculatedField = (f: Field): string => {
    const expression = f.values.length > 0 ? f.values[0] : f.value;
    try {
      const result = evaluateExpression?.(expression, nameValueMap);
      return typeof result === 'number' && !Number.isNaN(result) ? result.toString() : '';
    } catch (err) {
      console.warn('[buildAutoSaveFieldInputGroup] failed to evaluate calculated field', {
        fieldId: f.id,
        expression,
        err,
      });
      return '';
    }
  };

  return resolvedGroup.map((f) => {
    if (isCalculatedField(f)) {
      return {
        id: f.id,
        value: evaluateCalculatedField(f),
        values: [],
        entityID: entityID ?? f.entityID,
      };
    }

    const isTargetField = f.id === field.id;
    const baseValue = isTargetField ? value : f.value;
    const baseValues = isTargetField ? (values ?? []) : (f.values ?? []);

    return {
      id: f.id,
      value: isDateField(f) ? toISOSafeString(baseValue) : baseValue,
      values: isDateField(f) ? baseValues.map(toISOSafeString) : baseValues,
      entityID: entityID ?? f.entityID,
    };
  });
};

const handleAutoSaveUpdate = async ({
  fieldGroup,
  field,
  value,
  values,
  entityID,
  //note: `editIDs` would potentially needed here in the future, for now it's left as undefined
  formType,
  client,
  showSnackbar,
  onRefreshForm,
  isQuotesSearchEnabled = false,
}: Omit<UpdateNewFieldParams, 'setFormState' | 'setLoading'>): Promise<boolean> => {
  const result = await client
    .mutation<FormAutoSaveMutation, MutationFormAutoSaveArgs>(FormAutoSaveDocument, {
      input: {
        type: formType,
        fields: await buildAutoSaveFieldInputGroup(fieldGroup, field, value, values, entityID),
      },
    })
    .toPromise();

  const noLine = () => {
    if (field.type === InputType.Search) clearPendingQuoteLine();
    return false;
  };

  if (result.error) {
    return noLine();
  }

  const toast = result.data?.formAutoSave.toast;
  if (isQuotesSearchEnabled && toast) {
    showSnackbar(...toastToSnackbar(toast));
  }

  if (result.data?.formAutoSave.success && result.data.formAutoSave.hasRefetch) {
    onRefreshForm();
    return true;
  }

  return noLine();
};

const handleCascadeUpdate = async ({
  field,
  value,
  values,
  formType,
  client,
  editIDs,
  entityID,
  showSnackbar,
  setFormState,
}: Omit<UpdateNewFieldParams, 'fieldGroup' | 'onRefreshForm' | 'setLoading'>): Promise<void> => {
  const result = await client
    .query<CascadingSelectOptionsQuery, QueryCascadingSelectOptionsArgs>(CascadingSelectOptionsDocument, {
      input: {
        type: formType,
        field: {
          id: field.id,
          value,
          values: values ?? [],
          entityID: entityID ?? field.entityID,
        },
        ids: editIDs || [],
      },
    })
    .toPromise();

  if (result.error) {
    return;
  }

  const fieldsToReplace = result.data?.cascadingSelectOptions;
  if (fieldsToReplace) {
    setFormState((prev) => prev && replaceFormFields(prev, fieldsToReplace));
  }
};

// A search pick is a deliberate choice, so behind QuotesSearch it saves at once; typing keeps its batching.
export const isImmediateSaveField = (field: Pick<Field, 'type'>, isQuotesSearchEnabled = false) =>
  isQuotesSearchEnabled && field.type === InputType.Search;

export const isRemovedQuoteLine = (
  group: Pick<FieldGroup, 'fields'>,
  formType: FormType,
  isQuotesSearchEnabled = false,
) =>
  isQuotesSearchEnabled &&
  formType === FormType.SetQuote &&
  group.fields.some((field) => field.type === InputType.Numberstepper && field.value === '0');

export const pendingQuoteLine = (template: FieldGroup, label: string): FieldGroup => {
  const nameIndex = template.fields.findIndex((field) => field.type === InputType.Text);
  return {
    ...template,
    fields: template.fields.map((field, index) => {
      if (index === nameIndex) return { ...field, value: label, values: [label] };
      if (field.type === InputType.Numberstepper) return { ...field, value: '1', values: ['1'] };
      if (field.type === InputType.Hidden) return field;
      return { ...field, value: '', values: [] };
    }),
  };
};

export const handleFormFieldUpdateNew = async ({
  fieldGroup,
  field,
  value,
  values,
  entityID,
  editIDs,
  formType,
  client,
  setFormState,
  setLoading,
  onRefreshForm,
  showSnackbar,
  isQuotesSearchEnabled,
}: UpdateNewFieldParams): Promise<void> => {
  try {
    // Only a cascade update replaces fields and must block input. A plain auto-save must not
    // disable its own field mid-edit — a disabled input loses focus, kicking the user out while
    // they type. A refetching auto-save still blocks via the query's own fetching flag.
    if (field.hasCascade) {
      setLoading(true);
    }

    let refetchTriggered = false;

    if (field.hasAutoSave) {
      refetchTriggered = await handleAutoSaveUpdate({
        fieldGroup,
        field,
        value,
        values,
        entityID,
        formType,
        client,
        showSnackbar,
        onRefreshForm,
        isQuotesSearchEnabled,
      });
    }

    if (!refetchTriggered && field.hasCascade) {
      await handleCascadeUpdate({
        field,
        value,
        values,
        formType,
        editIDs,
        entityID,
        client,
        showSnackbar,
        setFormState,
      });
    }
  } catch (err) {
    showSnackbar({ message: 'Something went wrong while updating the field.' }, SnackbarVariant.ERROR);
  } finally {
    setLoading(false);
  }
};
